import React, { useState, useRef, useEffect } from 'react';
import { generateActivityContent, getSupportedFileTypes, pollQueryJobResult, cancelQueryJob } from '@/modules/ai-agent/agentApi';
import { getPreviewRenderer } from './previewRenderers';
import toast from 'react-hot-toast';

interface Message {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
  generatedContent?: any;
  attachments?: File[];
}

interface QuickPrompt {
  id: string;
  text: string;
  icon?: string;
}

interface AiChatDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  welcomeMessage?: string;
  welcomeSubMessage?: string;
  inputPlaceholder?: string;
  onContentGenerated: (content: any) => void;
  renderPreview?: (content: any) => React.ReactNode;
  contextData?: any;
  chainName?: string;
  quickPrompts?: QuickPrompt[];
}

// Quick Prompts Component
const QuickPrompts: React.FC<{
  prompts: QuickPrompt[];
  onPromptClick: (prompt: string) => void;
  disabled?: boolean;
}> = ({ prompts, onPromptClick, disabled = false }) => {
  if (!prompts || prompts.length === 0) return null;

  return (
    <div className="mb-4">
      <p className="text-xs text-gray-500 mb-2">Gợi ý nhanh:</p>
      <div className="flex flex-wrap gap-2">
        {prompts.map((prompt) => (
          <button
            key={prompt.id}
            onClick={() => onPromptClick(prompt.text)}
            disabled={disabled}
            className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full border border-gray-200 hover:border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            {prompt.icon && <span>{prompt.icon}</span>}
            {prompt.text}
          </button>
        ))}
      </div>
    </div>
  );
};

// File Upload Component
const FileUpload: React.FC<{
  files: File[];
  onFilesChange: (files: File[]) => void;
  disabled?: boolean;
  supportedTypes?: Record<string, string[]>;
  maxFileSize?: number;
}> = ({ files, onFilesChange, disabled = false, supportedTypes, maxFileSize = 10 * 1024 * 1024 }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    
    // Validate files
    const validFiles: File[] = [];
    const errors: string[] = [];
    
    selectedFiles.forEach(file => {
      // Check file size
      if (file.size > maxFileSize) {
        errors.push(`${file.name}: File size exceeds ${Math.round(maxFileSize / (1024 * 1024))}MB limit`);
        return;
      }
      
      // Check file type if supported types are provided
      if (supportedTypes) {
        const isSupported = Object.entries(supportedTypes).some(([mimeType, extensions]) => {
          return file.type === mimeType || extensions.some(ext => file.name.toLowerCase().endsWith(ext));
        });
        
        if (!isSupported) {
          errors.push(`${file.name}: File type not supported`);
          return;
        }
      }
      
      validFiles.push(file);
    });
    
    // Show errors if any
    if (errors.length > 0) {
      toast.error('File validation errors:\n' + errors.join('\n'), { style: { whiteSpace: 'pre-line' } });
    }
    
    // Add valid files to the list
    onFilesChange([...files, ...validFiles]);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    onFilesChange(newFiles);
  };

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          className="px-3 py-1.5 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded border border-blue-200 hover:border-blue-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Thêm tệp đính kèm
        </button>
        <span className="text-xs text-gray-500">
          Tối đa {Math.round(maxFileSize / (1024 * 1024))}MB mỗi tệp
        </span>
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        accept={supportedTypes ? Object.values(supportedTypes).flat().join(',') : undefined}
      />
      
      {files.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">Tệp đính kèm:</p>
          {files.map((file, index) => (
            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-sm text-gray-700">{file.name}</span>
                <span className="text-xs text-gray-500">({Math.round(file.size / 1024)}KB)</span>
              </div>
              <button
                type="button"
                onClick={() => removeFile(index)}
                disabled={disabled}
                className="text-red-500 hover:text-red-700 disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AiChatDialog: React.FC<AiChatDialogProps> = ({
  isOpen,
  onClose,
  title,
  welcomeMessage = "Bắt đầu cuộc trò chuyện với trợ lý AI",
  welcomeSubMessage = "Mô tả nội dung bạn muốn tạo",
  inputPlaceholder = "Mô tả nội dung bạn muốn tạo...",
  onContentGenerated,
  renderPreview,
  contextData,
  chainName,
  quickPrompts = []
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [supportedFileTypes, setSupportedFileTypes] = useState<Record<string, string[]>>({});
  const [maxFileSize, setMaxFileSize] = useState(10 * 1024 * 1024);
  const [polling, setPolling] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Load supported file types on component mount
  useEffect(() => {
    const loadSupportedTypes = async () => {
      try {
        const types = await getSupportedFileTypes();
        setSupportedFileTypes(types.supported_types);
        setMaxFileSize(types.max_file_size);
      } catch (error) {
        console.error('Failed to load supported file types:', error);
      }
    };
    
    if (isOpen) {
      loadSupportedTypes();
    }
  }, [isOpen]);
  
  // Handle AI generation
  const handleGenerate = async (prompt: string, files: File[] = []) => {
    return await generateActivityContent(
      prompt,
      chainName || 'content_analyzer',
      contextData,
      files
    );
  };

  // Handle quick prompt click
  const handleQuickPromptClick = (promptText: string) => {
    const currentValue = inputValue.trim();
    const newValue = currentValue 
      ? `${currentValue} ${promptText}` 
      : promptText;
    setInputValue(newValue);
    inputRef.current?.focus();
  };
  
  // Handle stopping the current query
  const handleStopQuery = async () => {
    if (currentJobId && polling) {
      try {
        await cancelQueryJob(currentJobId);
        setPolling(false);
        setCurrentJobId(null);
        const stopMessage: Message = {
          id: (Date.now() + 1).toString(),
          type: 'ai',
          content: 'Đã dừng tạo nội dung theo yêu cầu của bạn.',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, stopMessage]);
      } catch (err: any) {
        console.error('Error stopping query:', err);
        setError('Không thể dừng tạo nội dung. Vui lòng thử lại.');
      }
    }
  };
  
  // Focus on the input when dialog opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);
  
  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setMessages([]);
      setInputValue('');
      setError(null);
      setSelectedFiles([]);
      setCurrentJobId(null);
      setPolling(false);
    }
  }, [isOpen]);
  
  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Handle message submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
      attachments: selectedFiles.length > 0 ? [...selectedFiles] : undefined
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setPolling(true);
    setError(null);
    
    try {
      const jobId = await handleGenerate(userMessage.content, selectedFiles);
      setCurrentJobId(jobId);
      const agentResponse = await pollQueryJobResult(jobId, 1500, 180000); // 3 minute timeout
      setPolling(false);
      let generatedContent;
      try {
        generatedContent = JSON.parse(agentResponse.response);
      } catch (error) {
        // Try to extract JSON from the response if it contains other text
        const jsonMatch = agentResponse.response.match(/```json\s*([\s\S]*?)\s*```/) || 
                          agentResponse.response.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            const extractedJson = jsonMatch[1] || jsonMatch[0];
            generatedContent = JSON.parse(extractedJson);
          } catch (e) {}
        }
        if (!generatedContent) {
          generatedContent = { raw_response: agentResponse.response };
        }
      }
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: 'Tôi đã tạo nội dung cho bạn. Bạn có muốn áp dụng không?',
        timestamp: new Date(),
        generatedContent
      };
      setMessages(prev => [...prev, aiMessage]);
      setSelectedFiles([]);
    } catch (err: any) {
      setPolling(false);
      setCurrentJobId(null);
      console.error('Error generating content:', err);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: err.message || 'Không thể tạo nội dung. Vui lòng thử lại.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setError(err.message || 'Không thể tạo nội dung. Vui lòng thử lại.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle content application
  const handleApplyContent = (generatedContent: any) => {
    let contentToApply = generatedContent;
    
    // Handle JSON string responses
    if (typeof generatedContent === 'string') {
      try {
        contentToApply = JSON.parse(generatedContent);
      } catch (e) {
        console.warn('Failed to parse JSON string for content application:', e);
        contentToApply = generatedContent;
      }
    }
    
    // Handle different content structures
    if (contentToApply) {
      // If it has a nested content structure with sections, extract it
      if (contentToApply.content && contentToApply.content.sections) {
        contentToApply = contentToApply.content;
      }
      // If it has a nested content structure with activities, extract it
      else if (contentToApply.content && contentToApply.content.activities) {
        contentToApply = contentToApply.content;
      }
      // If it has a nested content structure with lessons, extract it
      else if (contentToApply.content && contentToApply.content.lessons) {
        contentToApply = contentToApply.content;
      }
      // If it has a nested content structure with exercises, extract it
      else if (contentToApply.content && contentToApply.content.exercises) {
        contentToApply = contentToApply.content;
      }
      // If it has a nested content structure with questions, extract it
      else if (contentToApply.content && contentToApply.content.questions) {
        contentToApply = contentToApply.content;
      }
    }
    
    console.log('Applying content:', contentToApply);
    onContentGenerated(contentToApply);
    onClose();
  };
  
  // Handle Escape key to close dialog
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  
  // Render message content
  const renderMessageContent = (message: Message) => {
    if (message.type === 'user') {
      return (
        <div className="space-y-2">
          <p className="text-white">{message.content}</p>
          {message.attachments && message.attachments.length > 0 && (
            <div className="text-blue-100 text-xs">
              <p>📎 Đính kèm {message.attachments.length} tệp:</p>
              {message.attachments.map((file, index) => (
                <p key={index} className="ml-2">• {file.name} ({Math.round(file.size / 1024)}KB)</p>
              ))}
            </div>
          )}
        </div>
      );
    }
    
    if (message.generatedContent) {
      let contentToCheck = message.generatedContent;
      
      // Handle JSON string responses for content check
      if (typeof message.generatedContent === 'string') {
        try {
          contentToCheck = JSON.parse(message.generatedContent);
        } catch (e) {
          contentToCheck = message.generatedContent;
        }
      }
      
      // More flexible content validation - check for any valid content structure
      const hasContent = contentToCheck && 
        Object.keys(contentToCheck).length > 0 && 
        !contentToCheck.error &&
        (
          // Check for module structure (sections)
          contentToCheck.sections || 
          (contentToCheck.content && contentToCheck.content.sections) ||
          // Check for activity content
          contentToCheck.activities ||
          (contentToCheck.content && contentToCheck.content.activities) ||
          // Check for lesson content
          contentToCheck.lessons ||
          (contentToCheck.content && contentToCheck.content.lessons) ||
          // Check for exercises/questions
          contentToCheck.exercises ||
          (contentToCheck.content && contentToCheck.content.exercises) ||
          contentToCheck.questions ||
          (contentToCheck.content && contentToCheck.content.questions) ||
          // Check for general content fields
          contentToCheck.title ||
          contentToCheck.name ||
          contentToCheck.description ||
          (contentToCheck.content && (contentToCheck.content.title || contentToCheck.content.name || contentToCheck.content.description))
        );
      
      // Debug logging to help understand the content structure
      console.log('Content validation debug:', {
        contentToCheck,
        hasContent,
        keys: contentToCheck ? Object.keys(contentToCheck) : [],
        hasSections: contentToCheck?.sections,
        hasContentSections: contentToCheck?.content?.sections,
        hasActivities: contentToCheck?.activities,
        hasContentActivities: contentToCheck?.content?.activities,
        hasTitle: contentToCheck?.title,
        hasName: contentToCheck?.name,
        hasDescription: contentToCheck?.description,
        contentType: typeof message.generatedContent,
        isString: typeof message.generatedContent === 'string'
      });
      
      // Determine the appropriate preview renderer based on chain name
      const getPreviewComponent = () => {
        if (renderPreview) {
          return renderPreview(message.generatedContent);
        }
        
              // Auto-detect content type based on structure
      let contentToRender = message.generatedContent;
      
      // Handle JSON string responses
      if (typeof message.generatedContent === 'string') {
        try {
          contentToRender = JSON.parse(message.generatedContent);
        } catch (e) {
          console.warn('Failed to parse JSON string:', e);
          contentToRender = message.generatedContent;
        }
      }
      
      // Check for nested structure: content.content.sections
      if (contentToRender.content && contentToRender.content.sections && Array.isArray(contentToRender.content.sections)) {
        const ModuleStructurePreview = getPreviewRenderer('module_structure');
        return <ModuleStructurePreview content={contentToRender.content} />;
      }
      
      // Check for direct structure: content.sections
      if (contentToRender.sections && Array.isArray(contentToRender.sections)) {
        const ModuleStructurePreview = getPreviewRenderer('module_structure');
        return <ModuleStructurePreview content={contentToRender} />;
      }
        
        // Default preview
        return (
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <h5 className="font-medium text-gray-900 mb-2">Nội dung đã tạo:</h5>
            <div className="max-h-[66vh] overflow-y-auto">
              <pre className="text-xs text-gray-700 whitespace-pre-wrap bg-gray-50 p-2 rounded border">
                {JSON.stringify(message.generatedContent, null, 2)}
              </pre>
            </div>
          </div>
        );
      };
      
      return (
        <div className="space-y-3">
          <p className="text-gray-800">{message.content}</p>
          <div>
            {getPreviewComponent()}
            {/* Always show apply button if there's generated content, regardless of structure */}
            {message.generatedContent && (
              <div className="mt-3">
                <button
                  onClick={() => handleApplyContent(message.generatedContent)}
                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                  title="Áp dụng nội dung đã tạo vào form"
                >
                  Áp dụng nội dung
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  Nội dung sẽ được áp dụng vào form bên dưới
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }
    
    return <p className="text-gray-800">{message.content}</p>;
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-4 py-3 bg-blue-600 text-white">
          <h3 className="font-medium">Trợ lý AI - {title}</h3>
          <button 
            onClick={onClose}
            className="text-white hover:text-gray-200"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              <p className="text-sm">{welcomeMessage}</p>
              <p className="text-xs mt-1">{welcomeSubMessage}</p>
            </div>
          )}
          
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`w-full px-3 py-2 rounded-lg ${
                  message.type === 'user'
                    ? 'bg-indigo-500 text-white'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {renderMessageContent(message)}
                <p className={`text-xs mt-1 ${
                  message.type === 'user' ? 'text-blue-100' : 'text-gray-500'
                }`}>
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))}
          
          {(isLoading || polling) && (
            <div className="flex justify-start">
              <div className="bg-gray-100 text-gray-800 px-3 py-2 rounded-lg">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm">
                    {polling ? 'Đang chờ AI tạo nội dung...' : 'Đang tạo nội dung...'}
                  </span>
                  {polling && (
                    <button
                      onClick={handleStopQuery}
                      className="ml-2 px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                      title="Dừng tạo nội dung"
                    >
                      Dừng
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input Area */}
        <div className="border-t border-gray-200 p-4">
          {/* Quick Prompts */}
          <QuickPrompts 
            prompts={quickPrompts}
            onPromptClick={handleQuickPromptClick}
            disabled={isLoading}
          />
          
          {/* File Upload */}
          <FileUpload
            files={selectedFiles}
            onFilesChange={setSelectedFiles}
            disabled={isLoading}
            supportedTypes={supportedFileTypes}
            maxFileSize={maxFileSize}
          />
          
          <form onSubmit={handleSubmit} className="flex space-x-2">
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={inputPlaceholder}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:ring-blue-500 focus:border-blue-500"
                disabled={isLoading}
              />
              {(isLoading || polling) && (
                <button
                  type="button"
                  onClick={handleStopQuery}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                  title="Dừng tạo nội dung"
                >
                  Dừng
                </button>
              )}
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
              disabled={isLoading || !inputValue.trim()}
            >
              Gửi
            </button>
          </form>
          
          {error && (
            <div className="text-red-500 text-xs mt-2 p-2 bg-red-50 border border-red-200 rounded">
              <strong>Lỗi:</strong> {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiChatDialog; 